import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import actions from '@src/redux/actions';
import { PAGINATION_LIMIT } from '@src/constants';
import { AUDIO_FILE_FORMAT } from '@src/constants/voice';
import {
  FACEBOOK,
  MAGNIFIC,
  MAX_BACKGROUND_MUSIC_FILE_SIZE,
} from '@src/constants/backgroundMusic';
import apis from '@src/apis';
import CustomTypography from '@src/new-components/Typography';
import { isValidFile, validateFileSize } from '@src/utils/checkValid';
import { COLOR } from '@src/styles/color';
import useBGMPolicyAgreement from '@src/hooks/useBGMPolicyAgreement';
import BGMTermsPolicyDialog from '@src/components/BGMTermsPolicyDialog';
import { StyledSlider } from '@src/components/VolumeNew/index.style';

import {
  Button,
  DialogActions,
  DialogContent,
  Typography,
  List,
  Box,
  Checkbox,
} from '@mui/material';
import Dialog from '@src/components/Dialog';

import CustomPagination from '@src/components/Pagination';
import ProcessHandler from '@src/components/ProcessHandler';
import BackgroundMusicItem from './BackgroundMusicItem';
import { StyledMusic } from './index.style';

const BackgroundMusic = ({
  open,
  currentBackgroundMusic,
  onChange,
  onClose,
  showUiChangeVolume,
  volume,
  max,
}) => {
  const [bgMusics, setBgMusics] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loadingMusic, setLoadingMusic] = useState(false);
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [selectedMusic, setSelectedMusic] = useState();

  const [presignedUrl, setPreSignedUrl] = useState(null);
  const [preUrlLoading, setPreUrlLoading] = useState(false);
  const [file, setFile] = useState();
  const [fileName, setFileName] = useState();
  const [formatError, setFormatError] = useState(false);
  const [oversize, setOverSize] = useState(false);
  const [volumeBackgroundMusic, setVolumeBackgroundMusic] = useState(
    volume || 0,
  );

  useEffect(() => {
    setVolumeBackgroundMusic(volume || 0);
  }, [volume]);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    isPolicyFeatureOn,
    needPolicyAgreement,
    isSubmitting: isAgreeingPolicy,
    agreePolicy,
  } = useBGMPolicyAgreement();
  const isBgMusicPolicyEnabled = isPolicyFeatureOn;

  const getUploadPresignedUrl = async (filename) => {
    setPreUrlLoading(true);
    const extension = filename.split('.').pop();
    const name = filename.split('.').shift().trim().replace(/\s+/g, '-');
    const data = await apis.backgroundMusic.getBgMusicPresignedUrlForUploading(
      `users/${name}`,
      extension,
    );
    setPreUrlLoading(false);
    if (data && data.status) setPreSignedUrl(data.result.url);
  };

  const fetchBackgroundMusics = async () => {
    setLoadingMusic(true);
    const data = await apis.backgroundMusic.getBackgroundMusics();
    setLoadingMusic(false);
    if (data && data.status) {
      const { backgroundMusics, metadata } = data.result;
      setTotal(metadata.total);
      setBgMusics(backgroundMusics);
      const bgMusicIndex = backgroundMusics.findIndex(
        (item) =>
          item.link === currentBackgroundMusic.link &&
          item.name === currentBackgroundMusic.name,
      );
      if (bgMusicIndex >= 0) {
        const newPage = Math.ceil((bgMusicIndex + 1) / PAGINATION_LIMIT);
        setPage(newPage);
      }
    }
  };

  const createBackgroundMusic = async (name, link) => {
    const data = await apis.backgroundMusic.createBackgroundMusic(name, link);
    setLoadingUpload(false);
    if (!data && !data?.status) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'uploadMusicFailure',
        }),
      );
    }
    const newBgMusic = data.result.result;
    const newBgMusics = [newBgMusic, ...bgMusics];
    setSelectedMusic(newBgMusic);
    setBgMusics(newBgMusics);
    dispatch(
      actions.noti.push({
        severity: 'success',
        message: 'uploadMusicSuccessfully',
      }),
    );
  };

  const handleChangePage = (value) => setPage(value);

  const handleSelectedMusic = (music) => setSelectedMusic(music);

  const handleRejectUpload = () => {
    setPage(1);
    setLoadingMusic(false);
    setSelectedMusic();
    setPreSignedUrl();
    setPreUrlLoading(false);
    setFile();
    setFileName();
    setFormatError(false);
    setOverSize(false);
    setVolumeBackgroundMusic(volume || 0);
  };

  const handleClose = () => {
    handleRejectUpload();
    onClose();
  };

  const handleChangeFile = (e) => {
    const { files } = e.target;
    if (!files || files.length !== 1) return;

    const { name } = files[0];
    if (!isValidFile(name, AUDIO_FILE_FORMAT)) {
      setFormatError(true);
      return;
    }

    if (!validateFileSize(files[0].size, MAX_BACKGROUND_MUSIC_FILE_SIZE)) {
      setOverSize(true);
      return;
    }

    setFileName(name);
    getUploadPresignedUrl(name);
    setFile(e.target.files[0]);
  };

  const handleUploadFile = async () => {
    if (!presignedUrl || !file) {
      return;
    }
    const { pathname, origin } = new URL(presignedUrl);
    setLoadingUpload(true);
    const data = await apis.upload.uploadByPresignedUrl(presignedUrl, file);
    if (data.status === 200) {
      const link = `${origin}${pathname}`;
      await createBackgroundMusic(fileName, link);
      handleRejectUpload();
    }
  };

  const handleApplyBackgroundMusic = () => {
    const isSelectedInList = bgMusics?.some(
      (item) =>
        item.link === selectedMusic?.link && item.name === selectedMusic?.name,
    );
    const name = isSelectedInList ? selectedMusic?.name : undefined;
    const link = isSelectedInList ? selectedMusic?.link : undefined;
    onChange(name, link, volumeBackgroundMusic);
    handleClose();
  };

  const handleUploadAgainFile = (e) => {
    handleRejectUpload();
    handleChangeFile(e);
  };

  useEffect(() => {
    if (open && !needPolicyAgreement) {
      fetchBackgroundMusics();
      dispatch(actions.audioPlayer.updateStatus(false));
    }
  }, [open, needPolicyAgreement]);

  useEffect(() => {
    if (showUiChangeVolume) {
      if (currentBackgroundMusic?.link && !selectedMusic?.link) {
        setSelectedMusic(currentBackgroundMusic);
      }
    } else if (currentBackgroundMusic) {
      setSelectedMusic(currentBackgroundMusic);
    }
  }, [currentBackgroundMusic, open, showUiChangeVolume]);

  const UploadError = () => (
    <div className="upload-wrapper">
      {formatError && (
        <Typography variant="subtitle2" color="error">
          {t('onlyUpload', { fileType: AUDIO_FILE_FORMAT.join(', ') })}
        </Typography>
      )}
      {oversize && (
        <Typography variant="subtitle2" color="error">
          {t('oversizeError')}
        </Typography>
      )}
      <label htmlFor="upload-music-file">
        <input
          id="upload-music-file"
          className="file-input"
          type="file"
          accept=".mp3,.wav"
          onChange={handleUploadAgainFile}
        />
        <Typography variant="subtitle2" className="upload-music-button">
          {t('reuploadBackgroundMusic')}
        </Typography>
      </label>
    </div>
  );

  const UploadFile = () => (
    <div className="upload-file-card">
      <div className="upload-file-info">
        <Typography
          variant="body2"
          className="upload-filename"
          title={file.name}
        >
          {file.name}
        </Typography>
      </div>
      <div className="button-wrapper">
        <ProcessHandler loading={loadingUpload} mr="12px" size={20}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            className="upload-button"
            onClick={handleUploadFile}
            data-id="confirm-upload-background-music"
          >
            {t('upload')}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            className="reject-button"
            onClick={handleRejectUpload}
            data-id="cancle-upload-background-music"
          >
            {t('cancel')}
          </Button>
        </ProcessHandler>
      </div>
    </div>
  );

  if (open && needPolicyAgreement) {
    return (
      <BGMTermsPolicyDialog
        open
        loading={isAgreeingPolicy}
        onClose={handleClose}
        onConfirm={agreePolicy}
      />
    );
  }

  return (
    <Dialog
      title={t('backgroundMusic')}
      open={open}
      fullWidth
      onClose={handleClose}
    >
      <StyledMusic>
        <DialogContent classes={{ root: 'dialog-content' }}>
          {showUiChangeVolume && (
            <div className="list-header">
              <CustomTypography
                variant="body-14-semibold"
                color={COLOR.darkGray}
                component="p"
              >
                {t('listBackgroundMusic')}
              </CustomTypography>
              {!file && !formatError && !oversize && (
                <label htmlFor="upload-music-file">
                  <input
                    id="upload-music-file"
                    className="file-input"
                    type="file"
                    accept=".mp3,.wav"
                    onChange={handleChangeFile}
                  />
                  <Typography
                    data-id="upload-background-music"
                    variant="subtitle2"
                    className="upload-music-button header-upload-button"
                  >
                    {t('uploadMusic')}
                  </Typography>
                </label>
              )}
            </div>
          )}
          <ProcessHandler loading={preUrlLoading} size={23} align="center">
            {!showUiChangeVolume && !file && !formatError && !oversize && (
              <label htmlFor="upload-music-file">
                <input
                  id="upload-music-file"
                  className="file-input"
                  type="file"
                  accept=".mp3,.wav"
                  onChange={handleChangeFile}
                />
                <Typography
                  data-id="upload-background-music"
                  variant="subtitle2"
                  className="upload-music-button"
                >
                  {t('uploadMusic')}
                </Typography>
              </label>
            )}
            {(formatError || oversize) && <UploadError />}
            {file && presignedUrl && <UploadFile />}
          </ProcessHandler>
          <ProcessHandler loading={loadingMusic} align="center">
            {bgMusics && bgMusics.length > 0 && (
              <div className="music-list-scroll">
                <List className="music-list" disablePadding>
                  {bgMusics
                    .slice(
                      page * PAGINATION_LIMIT - PAGINATION_LIMIT,
                      page * PAGINATION_LIMIT,
                    )
                    .map((musicItem) => {
                      const isSelected =
                        selectedMusic &&
                        selectedMusic.link === musicItem.link &&
                        selectedMusic.name === musicItem.name;
                      return (
                        <BackgroundMusicItem
                          key={musicItem.id}
                          backgroundMusic={musicItem}
                          isSelected={isSelected}
                          onSelectedMusic={handleSelectedMusic}
                          volume={volumeBackgroundMusic}
                          showUiChangeVolume={showUiChangeVolume}
                        />
                      );
                    })}
                </List>
              </div>
            )}
            {total > PAGINATION_LIMIT && (
              <CustomPagination
                count={Math.ceil(total / PAGINATION_LIMIT)}
                page={page}
                onChangePage={handleChangePage}
              />
            )}
            <div className="description">
              <Typography className="description-text">
                {t('backgroundMusicRecommendation')}
              </Typography>
              <ul className="description-list">
                <li>
                  <span>{t('backgroundMusicFacebookLabel')}</span>
                  <a href={FACEBOOK} target="_blank" rel="noreferrer">
                    {t('backgroundMusicLinkText')}
                  </a>
                </li>
                <li>
                  <span>{t('backgroundMusicMagnificLabel')}</span>
                  <a href={MAGNIFIC} target="_blank" rel="noreferrer">
                    {t('backgroundMusicLinkText')}
                  </a>
                </li>
              </ul>
            </div>
          </ProcessHandler>
          {showUiChangeVolume && (
            <div className="volume-container">
              <CustomTypography
                variant="body-14-semibold"
                color={COLOR.darkGray}
              >
                {t('backgroundMusicVolume')}
              </CustomTypography>
              <Box className="volume-wrapper">
                <StyledSlider
                  size="small"
                  aria-label="Volume"
                  color="primary"
                  value={volumeBackgroundMusic}
                  min={0}
                  max={max}
                  data-id="change-background-music-volume"
                  data-background-music-volume={volumeBackgroundMusic}
                  onChange={(_, value) => setVolumeBackgroundMusic(value)}
                />
                <CustomTypography
                  variant="body-14-medium"
                  color={COLOR.darkGray}
                >
                  {volumeBackgroundMusic}%
                </CustomTypography>
              </Box>
            </div>
          )}
        </DialogContent>
        {isBgMusicPolicyEnabled && (
          <div className="policy-footer">
            <Checkbox checked tabIndex={-1} className="policy-checkbox" />
            <Typography
              className="policy-checkbox-label"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: t('backgroundMusicCopyrightConsent'),
              }}
            />
          </div>
        )}
        <DialogActions classes={{ root: 'dialog-action' }}>
          <Button
            variant="contained"
            color="primary"
            className="apply-button"
            onClick={handleApplyBackgroundMusic}
            data-id="select-background-music"
            data-background-music={selectedMusic?.name}
          >
            {t('apply')}
          </Button>
        </DialogActions>
      </StyledMusic>
    </Dialog>
  );
};

export default BackgroundMusic;
